import * as React from 'react'
import { useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Link, useParams } from 'react-router-dom';
import Errors from '../../components/errors';
import Loader from '../../components/loader';
import { getBoreholes,         
         updateViewStyle,
         updateSortColumn,
         updateSortAscending,
         updateProjectName,
         updateBoreholeName,
         updateDepth,
         updateCreatedDate,
         updateLastEdited,
         updateCheckedEquipment,
         updateCheckedBoreholeStatuses,
         exportBoreholes,
         setClearCache,
         hubStatus } from "../../services/boreholesSlice";
import { useDeleteMutation } from '../../services/projectApi';
import { getClientTemplates
      } from './templateSlice';
import BoreholesList from './BoreholeListView';
import BoreholesCards from './BoreholeCardView';
import auth from '../../components/auth';
import { RootState } from '../../app/store';
import { useAppDispatch } from '../../app/hooks';
import { useSelector } from 'react-redux';
import BoreholeFilters from './filters/borehole-filters';
import BoreholeFilterLeftSideMenu from '../../components/side-menu/borehole-filter-left-side-menu';
import { useEquipmentQuery } from '../../services/lookupApi';
import { useBoreholeStatusesQuery } from '../../services/enumApi';
import { useClientTrackTypesHierarchyQuery, useClientImageTypesQuery } from '../../services/trackTypeApi';
import getConfig from '../../app/config';
import fetchProgress from 'fetch-progress';
import SelectField from '../../components/Input/select-field';
import ExportPanelMultiBorehole from '../../components/panels/log-viewer/export-panel-multi-borehole';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';

export default function Boreholes({setBorehole}: any) {    
    let { clientId, projectId } = useParams<any>();

    if (!clientId) {
      clientId = auth.getUserClientId();
    }
    
    const dispatch = useAppDispatch();

    const selectBorehole = (state: RootState) => state.borehole;
    const { 
      boreholes,      
      loading,
      error,
      continuationToken,
      nextPage,
      canAdd, 
      canEdit,
      canAccessFiles,
      canEditProject,
      canDeleteProject,
      viewStyle,
      sortColumn,
      sortAscending,
      projectName,
      boreholeName,
      minimumBoreholeDepth,
      maximumBoreholeDepth,
      minimumDepth,
      maximumDepth,
      dateCreatedStart,
      dateCreatedEnd,
      lastEditedStart,
      lastEditedEnd,
      checkedEquipment,
      checkedBoreholeStatuses,
      canAccessMap
    } = useSelector(selectBorehole);

    useEffect(() => {      
      dispatch(getClientTemplates({ templateType: 0, clientId, projectId }));      
    }, [clientId]);

    const [showExportPanel, setShowExportPanel] = useState<boolean>(false);
    const [showFilterPanel, setShowFilterPanel] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [exportCompleted, setExportCompleted] = useState<boolean>(false);
    const [exportUrl, setExportUrl] = useState<string>();
    const [exportProgress, setExportProgress] = useState<any>();
    const [exportFileName, setExportFileName] = useState<string>();
    const [exportJobId, setExportJobId] = useState<string | null>(null);
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [selectedBoreholeIds, setSelectedBoreholeIds] = useState<any>({});
    const [pageSize, setPageSize] = useState<any>(25);
    const [checkedTrackTypes, setCheckedTrackTypes] = useState<number[]>([]);
    const [selectedTemplateTrackType, setSelectedTemplateTrackType] = useState<number>(0);
    const [checkedImageTypes, setCheckedImageTypes] = useState<number[]>([]);
    const [checkedLithologyTypes, setCheckedLithologyTypes] = useState<number[]>([]);
    const [checkedStratigraphyTypes, setCheckedStratigraphyTypes] = useState<number[]>([]);
    const [checkedTextTypes, setCheckedTextTypes] = useState<number[]>([]);
    const [exportError, setExportError] = useState<any>(null);
    
    const hasFilters = projectName || boreholeName || minimumDepth > minimumBoreholeDepth || maximumDepth < maximumBoreholeDepth || dateCreatedStart || dateCreatedEnd || lastEditedStart || lastEditedEnd || checkedEquipment.length > 0 || checkedBoreholeStatuses.length > 0 || checkedTrackTypes.length > 0 || checkedImageTypes.length > 0 || checkedLithologyTypes.length > 0 || checkedStratigraphyTypes.length > 0 || checkedTextTypes.length > 0;
    const [showLeftSidePanel, setShowLeftSidePanel] = useState<boolean>(hasFilters);

    const { data: equipments, isLoading: equipmentsIsLoading } = useEquipmentQuery();
    const { data: boreholeStatuses, isLoading: boreholeStatusesIsLoading } = useBoreholeStatusesQuery();
    const { data: trackTypesHierarchyData, isLoading: trackTypesHierarchyDataIsLoading } = useClientTrackTypesHierarchyQuery(
      { clientId }, 
      { refetchOnMountOrArgChange: true }
    );
    const { data: imageTypes, isLoading: imageTypesIsLoading } = useClientImageTypesQuery(
      { clientId, imageViewer: false }, 
      { refetchOnMountOrArgChange: true }
    );
    const [deleteProject] = useDeleteMutation();

    const connectionRef = useRef<HubConnection | null>(null);
    const connectPromiseRef = useRef<Promise<HubConnection | null> | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const exportJobActiveRef = useRef(false);
    const disposedRef = useRef(false);
    const lifecycleStateRef = useRef<'idle' | 'connecting' | 'ready' | 'exporting' | 'disposing'>('idle');
    
    // Helper function to combine all track type IDs (excluding image types)
    const getAllTrackTypeIds = () => {
      return [...checkedTrackTypes, ...checkedLithologyTypes, ...checkedStratigraphyTypes, ...checkedTextTypes];
    };

    // useEffect(() => {        
    //   dispatch(clearFilters());
    // }, [clientId]);

    useEffect(() => {      
      dispatch(getBoreholes({ clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize, initial: true, projectName, boreholeName, minimumDepth, maximumDepth, trackTypeIds: getAllTrackTypeIds(), imageTypeIds: checkedImageTypes, pageLoad: true }));      
    }, [clientId]);

    useEffect(() => {
      setBorehole(null);
    }, [])

    const clearTimeoutRef = (timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const clearReconnectTimeout = () => clearTimeoutRef(reconnectTimeoutRef);
    const clearInactivityTimeout = () => clearTimeoutRef(inactivityTimeoutRef);
    const clearCloseDebounceTimeout = () => clearTimeoutRef(closeDebounceTimeoutRef);

    const removeExportHandlers = (hubConnection: HubConnection | null) => {
      if (!hubConnection) {
        return;
      }

      hubConnection.off("ExportMultipleBoreholesProgress");
      hubConnection.off("DownloadExportMultipleBoreholes");
    };

    const apiUrl = getConfig().apiUrl;

    const downloadFile = async (url: any, fileName: any, fileType: number = 3, updateProgress: any, completed: any) => 
    {      
      //const fileUrl = `${apiUrl}client/${clientId}/project/${projectId}/borehole/${boreholeId}/download?fileName=${encodeURIComponent(url)}&fileType=${fileType}&access_token=${auth.getTokenForImages()}`;
      const fileUrl = `${apiUrl}download/file/${encodeURIComponent(url)}?t=${auth.getTokenForImages()}`;
      //const fileUrl = `${apiUrl}client/${clientId}/project/${projectId}/borehole/${boreholeId}/download?fileName=F2bis.zip&fileType=${fileType}&access_token=${auth.getTokenForImages()}`;
      
      fetch(fileUrl, {
        method: 'GET',
        headers: new Headers({
          Authorization: "Bearer " + auth.getTokenForImages()
        })        
      })
      .then(
        fetchProgress({
          onProgress(progress: any) {
            updateProgress(progress.percentage);            
          },
          onError(err) {
            console.log(err);
          },
        })
      )      
      .then((response: any) => {
        return response.blob(); })
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(
          new Blob([blob]),
        );
        const link: any = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          fileName
        );
    
        // Append to html link element page
        document.body.appendChild(link);
    
        // Start download
        link.click();
    
        // Clean up and remove the link
        link.parentNode.removeChild(link);
        completed();
        
      })
      .catch((response) => {
        console.log(response);
        debugger;});

        return fileUrl;
    };

    const closeHubNow = async () => {
      clearReconnectTimeout();
      clearInactivityTimeout();
      clearCloseDebounceTimeout();

      const activeConnection = connectionRef.current;
      if (!activeConnection) {
        lifecycleStateRef.current = 'idle';
        dispatch(hubStatus({ connected: false }));
        return;
      }

      lifecycleStateRef.current = 'disposing';
      removeExportHandlers(activeConnection);

      try {
        if (activeConnection.state !== HubConnectionState.Disconnected) {
          await activeConnection.stop();
        }
      } catch (closeError) {
        console.error("Borehole List SignalR close error: ", closeError);
      } finally {
        if (connectionRef.current === activeConnection) {
          connectionRef.current = null;
        }
        if (!disposedRef.current) {
          setConnection(null);
        }
        dispatch(hubStatus({ connected: false }));
        lifecycleStateRef.current = 'idle';
      }
    };

    const closeHubIfAllowed = async (reason: 'unmount' | 'panel-close' | 'terminal' | 'inactivity') => {
      if (reason !== 'unmount' && exportJobActiveRef.current) {
        return;
      }

      await closeHubNow();
    };

    const scheduleInactivityClose = () => {
      clearInactivityTimeout();
      if (!showExportPanel || exportJobActiveRef.current) {
        return;
      }

      inactivityTimeoutRef.current = setTimeout(() => {
        void closeHubIfAllowed('inactivity');
      }, 120000);
    };

    const schedulePanelCloseDebounce = () => {
      clearCloseDebounceTimeout();
      if (exportJobActiveRef.current) {
        return;
      }

      closeDebounceTimeoutRef.current = setTimeout(() => {
        void closeHubIfAllowed('panel-close');
      }, 15000);
    };

    const registerExportHandlers = (hubConnection: HubConnection) => {
      removeExportHandlers(hubConnection);

      hubConnection.on("ExportMultipleBoreholesProgress", (percent: any, message: any, failed: any) => {
        setExportProgress({ progress: percent, message, failed });

        if (failed) {
          exportJobActiveRef.current = false;
          setIsExporting(false);
          lifecycleStateRef.current = 'ready';
          void closeHubIfAllowed('terminal');
        }
      });

      hubConnection.on("DownloadExportMultipleBoreholes", async (url: any, fileName: string) => {
        setExportFileName(fileName);
        const fileUrl = await downloadFile(url, fileName, 6, () => {
          setExportProgress({ progress: 100, message: "Completed exporting. Downloading...", failed: false });
        }, () => {
          exportJobActiveRef.current = false;
          setIsExporting(false);
          setExportCompleted(true);
        });

        setExportUrl(fileUrl);
        lifecycleStateRef.current = 'ready';
        await closeHubIfAllowed('terminal');
      });
    };

    const startSignalRConnection = async (hubConnection: HubConnection) => {
      lifecycleStateRef.current = 'connecting';

      try {
        await hubConnection.start();
        console.info("Borehole List SignalR Connected");
        dispatch(hubStatus({ connected: true }));
        if (!disposedRef.current) {
          setConnection(hubConnection);
        }
        lifecycleStateRef.current = exportJobActiveRef.current ? 'exporting' : 'ready';
        return true;
      } catch (startError) {
        console.error("Borehole List SignalR Connection Error: ", startError);
        dispatch(hubStatus({ connected: false }));
        lifecycleStateRef.current = 'idle';
        return false;
      }
    };

    const buildHubConnection = () => {
      const hubConnection = new HubConnectionBuilder()
        .withUrl(getConfig().boreholeListHubUrl, { accessTokenFactory: () => auth.getTokenForSignalR() })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            if (retryContext.elapsedMilliseconds < 600000) {
              return Math.random() * 10000;
            }

            return null;
          }
        })
        .configureLogging(LogLevel.Information)
        .build();

      hubConnection.onreconnecting(() => {
        dispatch(hubStatus({ connected: false }));
      });

      hubConnection.onreconnected(() => {
        dispatch(hubStatus({ connected: true }));
        lifecycleStateRef.current = exportJobActiveRef.current ? 'exporting' : 'ready';
      });

      hubConnection.onclose(async () => {
        dispatch(hubStatus({ connected: false }));

        if (disposedRef.current || lifecycleStateRef.current === 'disposing') {
          return;
        }

        if (!exportJobActiveRef.current) {
          await closeHubNow();
          return;
        }

        clearReconnectTimeout();
        reconnectTimeoutRef.current = setTimeout(async () => {
          if (disposedRef.current || lifecycleStateRef.current === 'disposing' || !exportJobActiveRef.current) {
            return;
          }

          await startSignalRConnection(hubConnection);
        }, 2000);
      });

      registerExportHandlers(hubConnection);
      return hubConnection;
    };

    const ensureExportHubConnected = async () => {
      clearCloseDebounceTimeout();

      const activeConnection = connectionRef.current;
      if (activeConnection?.state === HubConnectionState.Connected) {
        return activeConnection;
      }

      if (connectPromiseRef.current) {
        return await connectPromiseRef.current;
      }

      const connectPromise = (async () => {
        const nextConnection = activeConnection ?? buildHubConnection();
        if (!activeConnection) {
          connectionRef.current = nextConnection;
          if (!disposedRef.current) {
            setConnection(nextConnection);
          }
        }

        const started = await startSignalRConnection(nextConnection);
        if (!started) {
          if (connectionRef.current === nextConnection) {
            connectionRef.current = null;
          }
          if (!disposedRef.current) {
            setConnection(null);
          }
          return null;
        }

        return nextConnection;
      })();

      connectPromiseRef.current = connectPromise;

      try {
        return await connectPromise;
      } finally {
        connectPromiseRef.current = null;
      }
    };

    useEffect(() => {
      if (showExportPanel) {
        void ensureExportHubConnected();
        if (!exportJobActiveRef.current) {
          scheduleInactivityClose();
        }
      } else {
        clearInactivityTimeout();
        if (!exportJobActiveRef.current) {
          schedulePanelCloseDebounce();
        }
      }
    }, [showExportPanel]);

    useEffect(() => {
      if (isExporting) {
        clearInactivityTimeout();
        clearCloseDebounceTimeout();
        return;
      }

      if (showExportPanel && !exportJobActiveRef.current) {
        scheduleInactivityClose();
      }
    }, [isExporting, showExportPanel]);

    useEffect(() => {
      return () => {
        disposedRef.current = true;
        clearReconnectTimeout();
        clearInactivityTimeout();
        clearCloseDebounceTimeout();
        exportJobActiveRef.current = false;
        void closeHubIfAllowed('unmount');
      };
    }, []);

    const onSwitchViewList = () => {
      dispatch(updateViewStyle(1));
    };

    const onSwitchViewCard = () => {
      dispatch(updateViewStyle(0));
    };

    const tableSort = (column: string) => {
      let localSortAscending = true;
      if (sortColumn === column) {
        localSortAscending = !sortAscending; 
        dispatch(updateSortAscending({sortAscending: localSortAscending}));
      } else {
        dispatch(updateSortAscending({sortAscending: true}));
        dispatch(updateSortColumn({sortColumn: column}));
      }            

      dispatch(getBoreholes({ clientId, sortColumn: column, sortAscending: localSortAscending, continuationToken, nextPage, pageSize, initial: true, projectName, boreholeName, minimumDepth, maximumDepth, trackTypeIds: getAllTrackTypeIds(), imageTypeIds: checkedImageTypes, pageLoad: false }));
    };

    const loadMore = () => {
      dispatch(getBoreholes({ clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize, initial: false, projectName, boreholeName, minimumDepth, maximumDepth, trackTypeIds: getAllTrackTypeIds(), imageTypeIds: checkedImageTypes, pageLoad: false }));
    }

    const applyFilters = () => {      
      dispatch(getBoreholes({ clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize, initial: true, projectName, boreholeName, minimumDepth, maximumDepth, trackTypeIds: getAllTrackTypeIds(), imageTypeIds: checkedImageTypes, pageLoad: false }));
    };

    const clearFilters = () => {            
      dispatch(updateProjectName(""));
      dispatch(updateBoreholeName(""));
      dispatch(updateDepth({min: minimumBoreholeDepth, max: maximumBoreholeDepth}));
      dispatch(updateCreatedDate({start: null, end: null}));
      dispatch(updateLastEdited({start: null, end: null}));
      dispatch(updateCheckedEquipment([]));
      dispatch(updateCheckedBoreholeStatuses([]));
      setCheckedTrackTypes([]);
      setCheckedImageTypes([]);
      setCheckedLithologyTypes([]);
      setCheckedStratigraphyTypes([]);
      setCheckedTextTypes([]);
      dispatch(getBoreholes({ clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize, initial: true, projectName: null, boreholeName: null, minimumDepth: minimumBoreholeDepth, maximumDepth: maximumBoreholeDepth, trackTypeIds: [], imageTypeIds: [], pageLoad: false }));      
    };

    const onUpdateProjectName = (projectName: string) => {
      dispatch(updateProjectName(projectName));
    };

    const onUpdateBoreholeName = (boreholeName: string) => {
      dispatch(updateBoreholeName(boreholeName));
    };

    const onShowHideLeftSidePanel = () => {    
      if (showLeftSidePanel)  {        
        setShowExportPanel(false);
        setShowFilterPanel(false);
      } else {
        setShowFilterPanel(true);
      }

      setShowLeftSidePanel(!showLeftSidePanel);
    };

    const onShowFilterPanel = () => {            
      if (!showLeftSidePanel) {
        setShowLeftSidePanel(true);
      }    
      
      setShowExportPanel(false);
      setShowFilterPanel(true);
    };

    const onShowExportPanel = () => {      
      if (!showLeftSidePanel) {
        setShowLeftSidePanel(true);
      }    
      
      setIsExporting(false);
      setShowExportPanel(true);
      setShowFilterPanel(false);
      void ensureExportHubConnected();
    };

    const onSetDepth = (value : any) => {      
      dispatch(updateDepth(value));
    };
    
    const onSetCreatedDate = (value : any) => {
      dispatch(updateCreatedDate(value));
    };    

    const onSetLastEdited = (value : any) => {
      dispatch(updateLastEdited(value));
    };

    const onSetCheckedEquipment = (value : any) => {
      dispatch(updateCheckedEquipment(value))
    };

    const onSetCheckedBoreholeStatuses = (value : any) => {
      dispatch(updateCheckedBoreholeStatuses(value))
    };
    
    if (clientId === "list" || clientId === "add") {
      return null;
    }

    const onExportFile = async (headerType: any, deliminator: any, selectedTemplateId: any, dataExportType: any, selectedLithologyTrackTypeId: number, perMeter: number, includeTo: boolean, emailWhenCompleted: boolean) => {                  
      if (!selectedTemplateId) {
        setExportError({ message: "Please select a template." });
        return;
      }
      const boreholeIds = getSelectedBoreholeIds();
      
      if (boreholeIds.length === 0) {
        setExportError({ message: "Please select at least one borehole." });
        return;
      }

      if (dataExportType === 1 && !perMeter) {
        setExportError({ message: "Please enter meters." });
        return;
      }

      if (dataExportType === 2 && !selectedLithologyTrackTypeId) {
        setExportError({ message: "Please select a lithology." });
        return;
      }

      const activeConnection = await ensureExportHubConnected();

      if (!activeConnection?.connectionId) {
        setExportError({ message: "SignalR connection not ready yet. Please try again in a moment." } );
        scheduleInactivityClose();
        return;
      }

      exportJobActiveRef.current = true;
      lifecycleStateRef.current = 'exporting';
      clearInactivityTimeout();
      clearCloseDebounceTimeout();
      setIsExporting(true);
      setExportCompleted(false);
      setExportProgress({progress: null, message: "Pending", failed: false })
      setExportError(null);
      dispatch(exportBoreholes({ 
        clientId, 
        boreholeIds, 
        templateId: selectedTemplateId, 
        exportFileName, 
        headerType, 
        deliminator, 
        orderByBoreholeDepth: true, 
        dataExportType, 
        lithologyTrackTypeId: selectedLithologyTrackTypeId, 
        perMeter, 
        connectionId: activeConnection.connectionId, 
        includeTo, 
        emailWhenCompleted }))
        .unwrap()
        .then((result: any) => {                    
          if (result?.jobId) {
            setExportJobId(result.jobId);
          }
        })
        .catch((error: any) => {
          exportJobActiveRef.current = false;
          lifecycleStateRef.current = 'ready';
          setExportError(error);          
          setIsExporting(false);
          toast.error("Error exporting file.");
          console.error('Export failed:', error);
          void closeHubIfAllowed('terminal');
        });
    };

    const getSelectedBoreholeIds = () => {
      const keys = Object.keys(selectedBoreholeIds);
              
      let boreholeIds = [];
      for (var i = 0; i < keys.length; i ++)  {
          if (selectedBoreholeIds[keys[i]]) {                
            boreholeIds.push(keys[i]);
          }
      }       

      return boreholeIds;
  };

  const onChangePageSize = (value: number) => {
    setPageSize(value);
    dispatch(getBoreholes({ clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize: value, initial: true, projectName, boreholeName, minimumDepth, maximumDepth, trackTypeIds: getAllTrackTypeIds(), imageTypeIds: checkedImageTypes, pageLoad: false }));
  }

  const onDeleteProject = (projectId: any) => {
    confirmAlert({
            title: 'Confirm Delete Project',
            message: `Are you sure you want to delete project? All boreholes and data associated with this project will be deleted.`,
            buttons: [
              {
                label: 'Yes',
                onClick: () => {              
                  deleteProject({clientId, projectId})
                    .unwrap()
                    .then((response: any) => {
                      // refetch borehole list
                      dispatch(setClearCache({clearCache: true}));
                      dispatch(getBoreholes({ clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize, initial: true, projectName, boreholeName, minimumDepth, maximumDepth, trackTypeIds: getAllTrackTypeIds(), imageTypeIds: checkedImageTypes, pageLoad: false }));
                      toast.success("Successfully deleted project");              
                    })
                    .catch((error) => { toast.error("Error deleting project") });
                }
              },
              {
                label: 'No',
                onClick: () => {}
              }
            ]
          });   
  };

const leftPanelOffset = 300;

const pageSizes = [{name: "25", value: 25}, {name: "50", value: 50}, {name: "100", value: 100}];

const mapLink = `/client/${clientId}/project/${boreholes[0]?.projectId}/map-viewer`;

    return (   
      <section className="section">
        {loading && <Loader/>}
        <div>       
            <div className='mb-4'>                
                  <nav className="level">
                    <div className="level-left">
                      {/* <div className="level-item">
                        <nav className="breadcrumb" aria-label="breadcrumbs">
                          <ul>
                            <li></li>
                            <li><a>{data?.clientName}</a></li>
                            <li><Link to={`/client/${clientId}/project`} >{data?.projectName}</Link></li>
                            <li className="is-active"><a href="#" aria-current="page">Boreholes</a></li>
                          </ul>                
                        </nav>     
                      </div>
                      <div className="level-item">
                        View
                      </div>
                      <div className="level-item">
                        <div className="field">
                          <div className="select">
                            <select>
                              <option value={1}>All Boreholes</option>
                            </select>
                          </div>
                        </div>
                      </div> */}
                              
                    </div>
                    <div className="level-right">
                      <div className="level-item">
                        {canAdd && <Link to={`/client/${clientId}/project/${projectId}/borehole/add`} className="button mr-4">Add Borehole</Link>}
                      </div>
                      <div className="level-item">
                        {canAdd && <Link to={`/client/${clientId}/project/add`} className="button mr-4">Add Project</Link>}
                      </div>
                      <div className="level-item">
                        Page Size
                      </div>
                      <div className="level-item">
                        <SelectField values={pageSizes} value={pageSize} onChange={onChangePageSize}/>
                      </div>
                      <div className="level-item">
                        Layout
                      </div>
                      <div className="level-item">
                        <div className={`${viewStyle === 1 ? 'view-icon-background' : ''}`}>
                          <img src='/images/icons/List view icon.svg' width={24} className="view-icon" alt="List View" onClick={onSwitchViewList}/>                    
                        </div>
                      </div>
                      <div className="level-item">
                        <div className={`${viewStyle === 0 ? 'view-icon-background' : ''}`}>
                          <img src='/images/icons/Card view.svg' className="view-icon" alt="Card View" onClick={onSwitchViewCard}/>                    
                        </div>
                      </div>
                      {canAccessMap &&
                        <div className="level-item">                          
                            <Link to={mapLink}>Map</Link>                          
                        </div>
                      }
                    </div>
                  </nav>                 
            </div>                
            
            
            <div className="left-side-panel" style={{width: showLeftSidePanel ? 300 : 0 }}>
            {showFilterPanel && <BoreholeFilters 
                showLeftSidePanel={showLeftSidePanel} 
                setShowLeftSidePanel={setShowLeftSidePanel} 
                applyFilters={applyFilters}
                clearFilters={clearFilters}
                projectName={projectName} 
                setProjectName={onUpdateProjectName} 
                boreholeName={boreholeName} 
                setBoreholeName={onUpdateBoreholeName}
                minimumBoreholeDepth={minimumBoreholeDepth}
                maximumBoreholeDepth={maximumBoreholeDepth}
                minimumDepth={minimumDepth}
                maximumDepth={maximumDepth}
                setDepth={onSetDepth}
                setCreatedDate={onSetCreatedDate}
                dateCreatedStart={dateCreatedStart}
                dateCreatedEnd={dateCreatedEnd}
                setLastEditedDate={onSetLastEdited}
                lastEditedStart={lastEditedStart}
                lastEditedEnd={lastEditedEnd}
                hasFilters={hasFilters}
                leftPanelOffset={leftPanelOffset}
                equipments={equipments?.equipments}
                checkedEquipment={checkedEquipment}
                setCheckedEquipment={onSetCheckedEquipment}
                boreholeStatuses={boreholeStatuses}
                checkedBoreholeStatuses={checkedBoreholeStatuses}
                setCheckedBoreholeStatuses={onSetCheckedBoreholeStatuses}
                trackTypeCategories={trackTypesHierarchyData?.trackTypeCategories}
                checkedTrackTypes={checkedTrackTypes}
                setCheckedTrackTypes={setCheckedTrackTypes}
                selectedTemplateTrackType={selectedTemplateTrackType}
                setSelectedTemplateTrackType={setSelectedTemplateTrackType}
                allowedTrackCategories={[0, 1, 3, 4, 5, 7, 8, 9]}
                checkedImageTypes={checkedImageTypes}
                setCheckedImageTypes={setCheckedImageTypes}
                checkedLithologyTypes={checkedLithologyTypes}
                setCheckedLithologyTypes={setCheckedLithologyTypes}
                checkedStratigraphyTypes={checkedStratigraphyTypes}
                setCheckedStratigraphyTypes={setCheckedStratigraphyTypes}
                checkedTextTypes={checkedTextTypes}
                setCheckedTextTypes={setCheckedTextTypes}
                imageTypes={imageTypes?.imageTypes}
                />}
              {showExportPanel && <ExportPanelMultiBorehole 
                exportFile={onExportFile} 
                isExporting={isExporting} 
                setIsExporting={setIsExporting}
                progress={exportProgress}
                exportFileName={exportFileName} 
                setExportFileName={setExportFileName}
                downloaded={exportCompleted}
                url={exportUrl}
                showTemplates={true}
                jobId={exportJobId}
                error={exportError}
                 />}
              </div>     
              <BoreholeFilterLeftSideMenu 
                showLeftSidePanel={showLeftSidePanel} 
                onShowHideLeftSidePanel={onShowHideLeftSidePanel} 
                leftPanelOffset={leftPanelOffset} 
                onShowFilterPanel={onShowFilterPanel}
                onShowExportPanel={onShowExportPanel}
                showExportPanel={showExportPanel}
                showFilterPanel={showFilterPanel}
                />
              <div style={{ marginLeft: showLeftSidePanel ? leftPanelOffset + 20 : 20}} className="sidebar-main">
              <Errors error={error}/>       
                {viewStyle === 0 &&
                    <BoreholesCards boreholes={boreholes} 
                                    canEdit={canEdit} 
                                    canAccessFiles={canAccessFiles}
                                    clientId={clientId}
                                    moreToLoad={nextPage != null}
                                    loadMore={loadMore} />             
                }          
                {viewStyle === 1 && <BoreholesList boreholes={boreholes} 
                                            canEdit={canEdit} 
                                            canAccessFiles={canAccessFiles}
                                            canEditProject={canEditProject}
                                            canDeleteProject={canDeleteProject}
                                            clientId={clientId}
                                            tableSort={tableSort}
                                            sortColumn={sortColumn} 
                                            sortAscending={sortAscending}
                                            moreToLoad={nextPage != null}
                                            loadMore={loadMore}
                                            showSelection={showExportPanel}
                                            selectedBoreholeIds={selectedBoreholeIds} 
                                            setSelectedBoreholeIds={setSelectedBoreholeIds}
                                            loading={loading}
                                            deleteProject={onDeleteProject} />}
              </div>                 
        </div>
        </section>     
    )
  }